import { useQuery } from '@apollo/client';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import CalendlyModal from '@components/atoms/CalendlyModal';
import JuiceBox from '@components/features/landing-pages/JuiceBox';
import Button, { ButtonVariants } from '@components/interactive/Button';
import CTABox from '@components/interactive/CTABox';
import DefaultLayout from '@components/layout/layout/DefaultLayout';
import Advisors from '@components/pages/index/fsm/Advisors';
import BillOfRights from '@components/pages/index/fsm/BillOfRights';
import Clients from '@components/pages/index/fsm/Clients';
import FAQS from '@components/pages/index/fsm/FAQS';
import News from '@components/pages/index/fsm/News';
import Reviews from '@components/pages/index/fsm/Reviews';
import WhatToExpect from '@components/pages/index/fsm/WhatToExpect';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import { agentLookup } from '@constants/fsmTeam';
import { PhoneNumbers, getPhoneLink, hyphenatePhoneNumber } from '@constants/phoneNumbers';
import { DEFAULT_ROUND_ROBIN_SLUG } from '@utils/getCalendlySlugForUser';
import { shuffle } from '@utils/index';
import sortObjectsByEnum from '@utils/sortObjectsByEnum';
import { FsmColors } from 'chakra-ui/fsmTheme';
import GET_AGENT_AVAILABILITY from 'gql/GetAgentAvailability';
import Carriers from 'landing/Carriers';
import { useRouter } from 'next/router';
import { useState } from 'react';
import AgentPicker from './AgentPicker';
import { AvailabilityTag } from './AgentPicker/Card';

const formUtmForCalendly = (query) => {
  return {
    utmMedium: 'choose-your-agent',
    utmContent: query.utm_content,
    utmSource: query.utm_source,
    utmCampaign: query.utm_campaign,
    utmTerm: query.utm_term,
  };
};

export const formatAgents = (agents) => {
  // shuffle agents randomly.
  const shuffledAgents = shuffle([...agents]);
  // sort agents by availabilityTag
  const sortedAgents = sortObjectsByEnum(shuffledAgents, 'availabilityTag', [
    AvailabilityTag.AVAILABLE_TODAY,
    AvailabilityTag.AVAILABLE_TOMORROW,
    AvailabilityTag.AVAILABLE_LATER,
  ]);

  // annotate agents with info from agent lookup
  const annotatedAgents = sortedAgents.map((agent) => {
    return {
      ...agent,
      info: agentLookup[agent.agentProfile.scheduling_system_slug],
    };
  });

  // filter out hidden agents
  return annotatedAgents.filter((agent) => {
    if (!agent.info || !agent.info.showInChooseYourAgent) {
      return false;
    }
    return true;
  });
};

const ChooseYourAgent = () => {
  const router = useRouter();

  const utmParams = formUtmForCalendly(router.query);

  const [agentsToRender, setAgentsToRender] = useState(null);
  const [calendlyModalIsOpen, setCalendlyModelIsOpen] = useState(false);

  const { loading } = useQuery(GET_AGENT_AVAILABILITY, {
    nextFetchPolicy: 'no-cache',
    variables: {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    onCompleted: (data) => {
      const agents = data?.getAgentAvailability.agents;
      setAgentsToRender(formatAgents(agents));
    },
  });

  return (
    <DefaultLayout>
      <CalendlyModal
        isOpen={calendlyModalIsOpen}
        utmParams={utmParams}
        setIsOpen={setCalendlyModelIsOpen}
        eventSlug={DEFAULT_ROUND_ROBIN_SLUG}
      />
      <JuiceBox>
        {loading || !agentsToRender ? (
          <Flex pt={40} w="100%" justifyContent="center" alignItems="center">
            <Spinner />
          </Flex>
        ) : (
          <>
            <Header as="h2" bg="tan" p={2}>
              Talk to a Medicare Expert
            </Header>
            <AgentPicker agents={agentsToRender} utmParams={utmParams} />
            <Flex justifyContent="center">
              <Header as="h2" color={FsmColors.navy} bg="tan" p={8}>
                OR
              </Header>
            </Flex>
            <Box>
              <CTABox>
                <Header as="h3" color="white">
                  Book Next Available Agent
                </Header>
                <Body1 color="white">
                  All our agents are trained professionals and loved by their clients. If you just want to talk to
                  someone as soon as possible, call the phone number below:
                </Body1>
                <Button
                  bg="white"
                  variant={ButtonVariants.secondary}
                  href={getPhoneLink(PhoneNumbers.CHOOSE_YOUR_AGENT)}
                  width="100%"
                >
                  {`Call Us: ${hyphenatePhoneNumber(PhoneNumbers.CHOOSE_YOUR_AGENT)}`}
                </Button>
                <Button
                  color="white"
                  textDecoration="underline"
                  variant={ButtonVariants.tertiary}
                  onClick={() => setCalendlyModelIsOpen(true)}
                  width="100%"
                  sx={{
                    ':hover': {
                      color: 'white',
                      background: 'none',
                    },
                  }}
                >
                  Or Book Next Available
                </Button>
              </CTABox>
            </Box>
          </>
        )}
      </JuiceBox>
      <Carriers />
      <Reviews />
      <Clients />
      <BillOfRights />
      <WhatToExpect />
      <Advisors />
      <News />
      <FAQS />
    </DefaultLayout>
  );
};

export default ChooseYourAgent;
